import axios from "axios";
import Vue from "vue";
import i18next from "i18next";
import VueI18Next from "@panter/vue-i18next";
import store from "../vuex/store";
import moment from "moment";

import ca from "element-ui/lib/locale/lang/ca";
import de from "element-ui/lib/locale/lang/de";
import en from "element-ui/lib/locale/lang/en";
import es from "element-ui/lib/locale/lang/es";
import fr from "element-ui/lib/locale/lang/fr";
import it from "element-ui/lib/locale/lang/it";
import pl from "element-ui/lib/locale/lang/pl";
import pt from "element-ui/lib/locale/lang/pt";
import resourcesToBackend from "i18next-resources-to-backend";
import I18NextChainedBackend from "i18next-chained-backend";
import LocalforageBackend from "i18next-localforage-backend";
import * as localforage from "localforage";

//config file used for both website i18n load and weblate download
const i18nConfig = require(`./i18n-weblate.json`);

const elementLocales = {
  ca: ca,
  de: de,
  en: en,
  es: es,
  fr: fr,
  it: it,
  pl: pl,
  pt: pt
};

Vue.use(VueI18Next);

export const defaultLang = i18nConfig.fallbackLng;

// Default locale
let currentLocale = "";
const getLocale = store.getters.language;

if (getLocale) {
  currentLocale = getLocale;
} else {
  currentLocale = defaultLang;
  store.dispatch("updateLanguage", { lang: null }); // set to null in order to get default language from top-level domain
}

i18next.use(I18NextChainedBackend).init(
  {
    lng: currentLocale,
    fallbackLng: defaultLang,
    supportedLngs: [defaultLang].concat(i18nConfig.supportedLngs),
    partialBundledLanguages: true,
    resources: {},
    preload: [defaultLang, currentLocale],
    defaultNS: i18nConfig.defaultNS,
    ns: [i18nConfig.defaultNS]
      .concat(i18nConfig.fallbackNS)
      .concat(i18nConfig.librariesNS),
    fallbackNS: i18nConfig.fallbackNS.concat(i18nConfig.librariesNS),
    interpolation: {
      format: function(value, format, lng) {
        if (format === "uppercase") return value.toUpperCase();
        if (value instanceof Date) return moment(value).format(format);
        return value;
      },
      prefix: "{",
      suffix: "}",
      escapeValue: false
    },
    debug: false,
    backend: {
      backends: [
        LocalforageBackend,
        resourcesToBackend((language, namespace, callback) => {
          if ("element-ui" === namespace) {
            const resources = elementLocales[language];
            if (resources?.el?.datepicker?.year) {
              resources.el.datepicker.year = ""; //workaround "anno" el datepicker
            }
            callback(null, resources);
          } else {
            import(`../locales/${language}/${namespace}.json`)
              .then(resources => {
                callback(null, resources.default);
              })
              .catch(error => {
                callback(error, null);
              });
          }
        })
      ],
      backendOptions: [
        {
          driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
          version: 1,
          storeName: "mol-i18n",
          versions: i18nVersions(),
          expirationTime:
            process.env.NODE_ENV === "development" ? 0 : 24 * 60 * 60 * 1000 // 1 days
        }
      ]
    }
  },
  (err, t) => {
    if (err) return console.log("something went wrong loading", err);
  }
);

const i18n = new VueI18Next(i18next);

function i18nVersions() {
  return [defaultLang]
    .concat(i18nConfig.supportedLngs)
    .reduce((ac, a) => ({ ...ac, [a]: process.env.VUE_APP_VERSION }), {});
}

export function setClientLanguage(language: string) {
  //update language for i18n method
  if (language) {
    i18n.i18next.changeLanguage(language);
  }
  axios.defaults.headers.common["Accept-Language"] = language;
}

export function hasTranslation(key: string, options: any = {}) {
  return i18n.i18next.exists(key, options);
}

export default i18n;
